/**
 * Checkbox multi-select — post types, content types, authors, counted post types, group
 * membership pickers and the column menu all use it.
 *
 * Options are `{value,label}` pairs supplied by the caller; the component never derives an option
 * set from data it was not given.
 */

import { useId, type ReactNode } from 'react';
import { cx } from './cx';

export type CheckboxOption<T extends string | number> = {
  value: T;
  label: string;
  /** Small trailing note, e.g. a member count or a "counted" marker. */
  hint?: ReactNode;
  /** Second line under the label — the feed sidebar's "N members" / profile note (FEED_SPEC). */
  sub?: ReactNode;
  /** Rendered between the checkbox and the label; an avatar, typically. */
  lead?: ReactNode;
  disabled?: boolean;
};

export type CheckboxListProps<T extends string | number> = {
  options: readonly CheckboxOption<T>[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  /** Adds "Select all / Clear" affordances above the list. */
  withBulkActions?: boolean;
  emptyLabel?: string;
  className?: string;
};

export function CheckboxList<T extends string | number>({
  options,
  selected,
  onChange,
  withBulkActions = false,
  emptyLabel = 'Nothing to choose from.',
  className,
}: CheckboxListProps<T>) {
  const groupId = useId();
  const selectedSet = new Set<T>(selected);
  const offered = new Set<T>(options.map((option) => option.value));
  /**
   * Selections for values the current option list does not offer (filtered out by a search box)
   * survive every edit — narrowing the visible list must never silently drop a choice.
   */
  const preserved = selected.filter((value) => !offered.has(value));

  const emit = (chosen: Set<T>) => {
    onChange([...preserved, ...options.map((option) => option.value).filter((value) => chosen.has(value))]);
  };

  const toggle = (value: T) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    emit(next);
  };

  if (options.length === 0) {
    return <p className={cx('muted', className)}>{emptyLabel}</p>;
  }

  return (
    <div className={cx('checklist', className)}>
      {withBulkActions ? (
        <div className="checklist__actions">
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={() =>
              emit(new Set(options.filter((option) => !option.disabled).map((option) => option.value)))
            }
          >
            Select all
          </button>
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={() => emit(new Set<T>())}
          >
            Deselect all
          </button>
        </div>
      ) : null}
      <ul className="checklist__items">
        {options.map((option) => {
          const id = `${groupId}-${option.value}`;
          return (
            <li key={option.value}>
              <label className="checklist__item" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={selectedSet.has(option.value)}
                  disabled={option.disabled}
                  onChange={() => toggle(option.value)}
                />
                {option.lead}
                {/* One-line rows keep their exact markup: the label is still the flex child that
                    grows, so nothing that used this list before renders differently. */}
                {option.sub === undefined ? (
                  <span className="checklist__label">{option.label}</span>
                ) : (
                  <span className="checklist__text">
                    <span className="checklist__label">{option.label}</span>
                    <span className="checklist__sub">{option.sub}</span>
                  </span>
                )}
                {option.hint !== undefined ? (
                  <span className="checklist__hint">{option.hint}</span>
                ) : null}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CheckboxList;
